import { TAU, clamp, damp, lerp } from '../core/math';
import { RNG, rr } from '../core/rng';
import { enemies } from '../entities/enemies';
import { SFX } from '../platform/audio';
import { scene } from '../render/scene';
import { updateWarnFx } from '../render/warn-fx';
import { STATE, areaDamage, damageEnemy, heal, hitstop, nearestEnemy, popDmg, shake, spawnDebris, tmpV } from './fx';
import { hurtPlayer } from './player';
import { gainXp } from './level';
import { spawnBullet } from './weapons';
import { ARENA, groundYAt, inArena, pointBlocked } from '../world/arena';
import { P, slashFx } from '../world/player';
import { TIERS, offerChest } from './chest';
import { bullets, chests, debris, ebullets, mines, missiles, orbs, rings, shocks, strikes } from '../world/pools';
import { VC, fxExhaust, fxExplode, fxImpact, fxOrbitalHit, fxSparks } from '../render/weapon-fx';
/* ============================================================
   投射物 / 拾取 / 特效
   ============================================================ */
export function bulletHitEnemy(o) {
  for (var j = 0; j < enemies.length; j++) {
    var e = enemies[j];
    if (o.hitList.indexOf(e) >= 0) continue;
    var dx = e.pos.x - o.m.position.x, dz = e.pos.z - o.m.position.z;
    var rr2 = e.r + o.hitR;
    if (dx * dx + dz * dz < rr2 * rr2) return e;
  }
  return null;
}
export function updateProjectiles(dt) {
  var i, o;
  for (i = 0; i < bullets.items.length; i++) {
    o = bullets.items[i]; if (!o.alive) continue;
    o.life += dt;
    o.m.position.addScaledVector(o.v, dt);
    var hit = bulletHitEnemy(o);
    if (hit) {
      var ang = Math.atan2(o.v.x, o.v.z);
      /* 命中點傳下去 —— Boss 的可破壞部位用它判定「這一發打在哪一側」
         （`sim/boss-parts.ts`）。子彈是唯一有精確命中點的傷害來源，
         範圍類的一律打在本體上。 */
      damageEnemy(hit, o.dmg, o.crit, Math.sin(ang) * 3.2, Math.cos(ang) * 3.2, 'vulcan',
        o.m.position.x, o.m.position.z, o.who);
      spawnDebris(o.m.position.x, o.m.position.y, o.m.position.z, 0xffd9a0, 2, 0.35, 0.14);
      /* **命中特效**：在這一行之前，打中敵人跟打空是同一個畫面（只有傷害數字
         與音效）。爆閃 + 往回噴的火花是「這一發有打到」最直接的回饋，
         暴擊另外給金色十字。每幀有配額，300 隻同屏時會自己節流。 */
      fxImpact(o.m.position.x, o.m.position.y, o.m.position.z, ang, o.crit);
      o.hitList.push(hit);
      if (o.chain > 0) {
        var nxt = null, bd = 9;
        for (var q = 0; q < enemies.length; q++) {
          var e2 = enemies[q]; if (o.hitList.indexOf(e2) >= 0) continue;
          var d2 = Math.hypot(e2.pos.x - o.m.position.x, e2.pos.z - o.m.position.z);
          if (d2 < bd) { bd = d2; nxt = e2; }
        }
        if (nxt) {
          var na = Math.atan2(nxt.pos.x - o.m.position.x, nxt.pos.z - o.m.position.z);
          var nb = spawnBullet(o.m.position.x, o.m.position.z, na, o.dmg * 0.8, o.crit, 52, 0, o.chain - 1, 0.5, undefined, o.who);
          if (nb) { for (var h = 0; h < o.hitList.length; h++) nb.hitList.push(o.hitList[h]); }
        }
        o.alive = false; scene.remove(o.m); continue;
      }
      if (o.pierce > 0) { o.pierce--; o.dmg *= 0.75; }
      else { o.alive = false; scene.remove(o.m); continue; }
    }
    /* **高度要帶進去**（`atY`）。不帶的話站在 6.5 高的平台上開火，
       子彈會被腳邊那根 5 高的柱子吃掉 —— 而畫面上它明明從柱頂飛過。
       決定 6 說高處給的就是「視野與射界」，射界要真的存在。 */
    if (o.hitR < 2 && pointBlocked(o.m.position.x, o.m.position.z, 0.1, o.m.position.y)) {
      spawnDebris(o.m.position.x, 2.1, o.m.position.z, 0xffd9a0, 2, 0.3, 0.14);
      // 打到牆：火花朝反方向濺開。玩家因此看得出「這一發被擋掉了」
      fxSparks(o.m.position.x, o.m.position.y, o.m.position.z, 3,
        Math.atan2(-o.v.x, -o.v.z), 0.9, VC.spark, 11);
      o.alive = false; scene.remove(o.m); continue;
    }
    /* 場邊反彈（反射彈）。**沿法線鏡射**而不是隨便轉個角度。
       場地改成方形之後（`world/arena.ts`），法線就是被撞到的那一面的軸 ——
       撞到 +x 那面就翻 vx、撞到 −z 那面就翻 vz，角落兩個都翻。 */
    var px = o.m.position.x, pz = o.m.position.z;
    var outX = Math.abs(px) > ARENA - 2.5, outZ = Math.abs(pz) > ARENA - 2.5;
    if (o.bounce > 0 && (outX || outZ)) {
      if (outX) { o.v.x = -o.v.x; o.m.position.x = (px < 0 ? -1 : 1) * (ARENA - 2.7); }
      if (outZ) { o.v.z = -o.v.z; o.m.position.z = (pz < 0 ? -1 : 1) * (ARENA - 2.7); }
      o.bounce--; o.hitList.length = 0;          // 反彈後可以再打同一隻
      spawnDebris(o.m.position.x, 1.4, o.m.position.z, 0xaee4ff, 3, 0.5, 0.2);
      if (o.split) {
        var bAng = Math.atan2(o.v.x, o.v.z);
        var sb = spawnBullet(o.m.position.x, o.m.position.z, bAng + 0.5, o.dmg * 0.7, o.crit, 48, 1, 0, 0.5, undefined, o.who);
        if (sb) { sb.bounce = o.bounce; sb.ttl = o.ttl; }
      }
      continue;
    }
    if (o.life > (o.ttl || 1.1) || !inArena(o.m.position.x, o.m.position.z, -3)) { o.alive = false; scene.remove(o.m); }
  }
  for (i = 0; i < ebullets.items.length; i++) {
    o = ebullets.items[i]; if (!o.alive) continue;
    o.life += dt;
    o.m.position.addScaledVector(o.v, dt);
    o.m.rotation.y += dt * 6; o.m.rotation.x += dt * 4;
    var ddx = o.m.position.x - P.pos.x, ddz = o.m.position.z - P.pos.z;
    /* 高度也要對得上。**沒有這一段的話水平飛的子彈會打到 12 單位高的你**，
       而畫面上它明明從你腳下過去 —— 敵彈出膛時已經朝你當時的高度修正過
       （`eShoot`），所以遠程照樣打得到空中的目標，這裡只是不讓它作弊。 */
    var ddy = o.m.position.y - (P.y + 1.6);
    if (ddx * ddx + ddz * ddz < 2.0 && ddy * ddy < 5.8) {
      hurtPlayer(o.dmg);
      spawnDebris(o.m.position.x, 1.8, o.m.position.z, 0xff6a80, 3, 0.4, 0.16);
      o.alive = false; scene.remove(o.m); continue;
    }
    if (pointBlocked(o.m.position.x, o.m.position.z, 0.1, o.m.position.y)) { o.alive = false; scene.remove(o.m); continue; }
    if (o.life > 5 || !inArena(o.m.position.x, o.m.position.z, -3)) { o.alive = false; scene.remove(o.m); }
  }
  for (i = 0; i < missiles.items.length; i++) {
    o = missiles.items[i]; if (!o.alive) continue;
    o.life += dt;
    /* `arc = 1` 是**無動力彈道**（TITAN-3 的榴彈與分裂彈）：沒有目標、
       只吃重力，所以尾焰噴口要關掉、排氣煙也不吐 —— 一顆沒有推力的砲彈
       拖著火焰跑，讀起來會變回飛彈。`children[4]` 是池子建的尾焰錐
       （`world/pools.ts`）。每幀設一次而不是在發射端設：池化物件會被不同
       武裝輪流借用，漏設一處就會有一顆永遠不熄火的砲彈。 */
    o.m.children[4].visible = !o.arc;
    if (o.target && !o.target.alive) o.target = nearestEnemy(o.m.position, 40);
    if (o.target && o.life > 0.22) {
      tmpV.set(o.target.pos.x - o.m.position.x, o.target.T.y + o.target.r * 0.6 - o.m.position.y, o.target.pos.z - o.m.position.z).normalize();
      o.v.lerp(tmpV.multiplyScalar(Math.min(46, 16 + o.life * 46)), Math.min(1, dt * 7));
    } else o.v.y -= 22 * dt;
    o.m.position.addScaledVector(o.v, dt);
    o.m.lookAt(o.m.position.x + o.v.x, o.m.position.y + o.v.y, o.m.position.z + o.v.z);
    /* 排氣煙：每 1/13 秒吐一團。**不用緞帶** —— 加法混色的緞帶不論調多暗
       都是「一道光劃過去」，而飛彈該留下的是會擴散、會擋住背後的煙
       （2026-08-05：使用者回報飛彈魔法感太重）。
       用「跨過時間格線」判斷而不是自己開一個計時器欄位，同 `mech-anim` 的排氣。 */
    if (!o.arc && ((o.life * 22) | 0) !== (((o.life - dt) * 22) | 0)) {
      fxExhaust(o.m.position.x, o.m.position.y, o.m.position.z, o.scale);
    }
    var boom = false;
    if (o.target && o.target.alive) {
      var mx = o.target.pos.x - o.m.position.x, my = o.target.T.y + o.target.r * 0.6 - o.m.position.y, mz = o.target.pos.z - o.m.position.z;
      if (mx * mx + my * my + mz * mz < (o.target.r + 0.9) * (o.target.r + 0.9)) boom = true;
    }
    if (o.m.position.y < 0.3 || o.life > 4.5) boom = true;
    if (boom) {
      spawnDebris(o.m.position.x, Math.max(0.4, o.m.position.y), o.m.position.z, 0xffb454, 6, 0.8, 0.22 * o.scale);
      fxExplode(o.m.position.x, Math.max(0.5, o.m.position.y), o.m.position.z, VC.fire, o.radius * 0.9 * o.scale, 1);
      areaDamage(o.m.position.x, o.m.position.z, o.radius, o.dmg * (RNG.next() < P.s.crit ? P.s.critMul : 1), 0xffb454, undefined, o.who);
      SFX.kill(o.m.position.x, o.m.position.z, true); shake(0.12);
      if (o.split > 0) {
        for (var sp = 0; sp < o.split; sp++) {
          var sm = missiles.get(); if (!sm) break;
          sm.m.position.copy(o.m.position); sm.m.position.y = Math.max(1.5, sm.m.position.y);
          sm.m.scale.setScalar(0.6); sm.scale = 0.6;
          var sa = (sp / o.split) * TAU + rr(0, 1);
          sm.v.set(Math.sin(sa) * 14, 9, Math.cos(sa) * 14);
          sm.target = nearestEnemy(sm.m.position, 34); sm.life = 0;
          /* 子彈一律是**有動力**的（會自己找目標），所以 `arc` 要清掉 ——
             母彈是榴彈（arc = 1）時不清的話，分裂出來的小彈會拿著
             「無動力」的旗標追蹤，尾焰永遠不亮。 */
          sm.dmg = o.dmg * 0.45; sm.radius = 2.4; sm.split = 0; sm.who = o.who; sm.trail = -1; sm.arc = 0;
          scene.add(sm.m);
        }
      }
      o.alive = false; scene.remove(o.m);
    }
  }
  for (i = 0; i < mines.items.length; i++) {
    o = mines.items[i]; if (!o.alive) continue;
    o.life += dt; o.arm -= dt;
    o.m.scale.setScalar(Math.min(1, o.m.scale.x + dt * 5));
    o.m.rotation.y += dt * 2;
    o.m.children[2].position.y = 0.5 + Math.sin(o.life * 6) * 0.1;
    o.m.children[2].rotation.y = o.life * 1.6;
    /* 感應範圍、爆炸半徑、存在時間都改成**每顆自己帶**，不再寫死。
       這樣「範圍」與「持續」兩條統計軸才有東西可以動，
       地雷 Lv3 的「接觸雷 → 感應雷」門檻也才做得出來。
       沒設就退回原本的數字，所以舊的呼叫端行為完全不變。 */
    var trigR = o.trigR || 2.8, blast = o.blast || 5.2, ttl = o.ttl || 14;
    /* 感應範圍框：布防中（`arm > 0`）快閃、布防完成後轉慢速呼吸。
       兩段不同的節奏讓「還不能踩」與「已經生效」分得開。 */
    var tg = o.m.children[3];
    if (tg) {
      tg.scale.setScalar(trigR);
      tg.rotation.z += dt * 0.6;
      tg.material.opacity = o.arm > 0
        ? 0.34 * (Math.sin(o.life * 34) * 0.5 + 0.5)
        : 0.20 * (0.7 + 0.3 * Math.sin(o.life * 2.6));
    }
    var trig = false;
    if (o.arm <= 0) {
      for (var mi2 = 0; mi2 < enemies.length; mi2++) {
        var em = enemies[mi2];
        if (Math.hypot(em.pos.x - o.m.position.x, em.pos.z - o.m.position.z) < trigR + em.r) { trig = true; break; }
      }
    }
    if (trig || o.life > ttl) {
      if (trig) {
        areaDamage(o.m.position.x, o.m.position.z, blast, o.dmg, 0xffb454, undefined, o.who); SFX.kill(o.m.position.x, o.m.position.z, true); shake(0.14);
        fxExplode(o.m.position.x, 0.8, o.m.position.z, VC.fire, blast * 0.8, 1.1);
      }
      spawnDebris(o.m.position.x, 0.6, o.m.position.z, 0xffb454, 5, 0.7, 0.2);
      o.alive = false; scene.remove(o.m);
    }
  }
  for (i = 0; i < strikes.items.length; i++) {
    o = strikes.items[i]; if (!o.alive) continue;
    o.life += dt;
    o.ring.rotation.z += dt * 2;
    if (!o.fired) {
      var kk = o.life / o.delay;
      o.ring.material.opacity = 0.45 + Math.abs(Math.sin(o.life * 22)) * 0.5;
      o.ring.scale.setScalar(o.radius * lerp(1.5, 1, clamp(kk, 0, 1)));
      if (o.life >= o.delay) {
        o.fired = true;
        o.col.material.opacity = 0.9;
        areaDamage(o.m.position.x, o.m.position.z, o.radius, o.dmg, 0x67c8ff, undefined, o.who);
        SFX.boom(); shake(0.6); hitstop(0.4);
        spawnDebris(o.m.position.x, 1, o.m.position.z, 0xaee4ff, 10, 1.2, 0.3);
        /* 光柱落地：現況只有圓柱的不透明度跳到 0.9，讀不出「砸下來」。
           爆閃 + 往上的塵柱 + 地面環才有重量。 */
        fxOrbitalHit(o.m.position.x, o.m.position.z, o.radius, 0x9fe0ff);
      }
    } else {
      o.col.material.opacity = Math.max(0, o.col.material.opacity - dt * 3);
      o.ring.material.opacity = Math.max(0, o.ring.material.opacity - dt * 3);
      if (o.col.material.opacity <= 0) { o.alive = false; scene.remove(o.m); }
    }
  }
  for (i = 0; i < shocks.items.length; i++) {
    o = shocks.items[i]; if (!o.alive) continue;
    o.r += o.spd * dt;
    o.m.scale.setScalar(o.r);
    o.m.material.opacity = 0.9 * (1 - o.r / o.rmax);
    var pd = Math.hypot(P.pos.x - o.m.position.x, P.pos.z - o.m.position.z);
    if (!o.hit && Math.abs(pd - o.r) < 1.6) { o.hit = true; hurtPlayer(o.dmg); }
    if (o.r >= o.rmax) { o.alive = false; scene.remove(o.m); }
  }
}

export function updatePickups(dt) {
  /* 寶箱：不吸、不追、不消失。玩家得自己走過去 ——
     那趟路就是它的設計重點（畫面最亂的時候要不要為它穿過敵群）。 */
  for (var ci = 0; ci < chests.items.length; ci++) {
    var c = chests.items[ci]; if (!c.alive) continue;
    c.life += dt;
    c.m.rotation.y += dt * 0.9;
    c.m.position.y = groundYAt(c.m.position.x, c.m.position.z) + 0.15 + Math.sin(c.life * 2.4) * 0.12;
    var cdx = P.pos.x - c.m.position.x, cdz = P.pos.z - c.m.position.z;
    if (Math.hypot(cdx, cdz) < 2.2) {
      if (c.mw) {
        /* 武裝強化箱（精英首領的小機率掉落）：**一次機體武裝強化**，
           不是三次升級。走 `bossUp` 這個佇列，跟 Boss 擊破同一條路徑。 */
        P.bossUp++;
        popDmg(P.pos.x, 5, P.pos.z, '武裝強化', 'heal');
      } else {
        /* **三選一（金箱五選三）**。2026-08-04 晚把拉霸拔掉之後，
           開箱只是「擲出選項」，套用在玩家選了之後（`ui/levelup.ts`）——
           不需要拉霸那道「先套用再顯示」的保險，因為介面會暫停模擬。 */
        var off = offerChest(c.tier, STATE.t / 60);
        if (off.ids.length) {
          P.chestOffer = off.ids; P.chestPicks = off.picks; P.chestTier = c.tier;
          var tn = (TIERS[c.tier] || TIERS.silver).n;
          popDmg(P.pos.x, 5, P.pos.z,
            tn + '　' + off.ids.length + ' 選 ' + off.picks, 'heal');
        } else {
          /* 武裝與被動全滿級又滿槽、也沒有進化可拿。VS 在這個情況把箱子
             換成金幣袋 —— 同一個意思：**不要給玩家一個空箱子**，
             那是「走過去」這件事最差的結局。這裡換成回血。 */
          heal(60);
          popDmg(P.pos.x, 5, P.pos.z, '緊急補給', 'heal');
        }
      }
      SFX.level(); shake(0.3);
      c.alive = false; scene.remove(c.m);
    }
  }

  for (var i = 0; i < orbs.items.length; i++) {
    var o = orbs.items[i]; if (!o.alive) continue;
    o.life += dt;
    var dx = P.pos.x - o.m.position.x, dz = P.pos.z - o.m.position.z;
    var d = Math.max(0.001, Math.hypot(dx, dz));
    if (d < P.s.magnet || o.pulling || o.life > 8) {
      o.pulling = true;
      var pull = Math.min(36, 9 + o.life * 6);
      o.v.x = damp(o.v.x, dx / d * pull, 9, dt);
      o.v.z = damp(o.v.z, dz / d * pull, 9, dt);
      o.v.y = damp(o.v.y, (P.y + 1.2 - o.m.position.y) * 4, 6, dt);
    } else {
      /* 經驗球掉在**它自己腳下的地面**上，被吸的時候飛向**玩家的高度** ——
         寫死 0.55 的話高台上打死的敵人會把球掉進平台裡，而玩家站在高台上
         看著球從腳下的地板穿過去。 */
      o.v.y -= 26 * dt;
      var og = groundYAt(o.m.position.x, o.m.position.z) + 0.55;
      if (o.m.position.y < og) { o.m.position.y = og; o.v.y = Math.abs(o.v.y) * 0.42; o.v.x *= 0.7; o.v.z *= 0.7; }
    }
    o.m.position.addScaledVector(o.v, dt);
    o.m.rotation.y += dt * 3; o.m.rotation.x += dt * 2;
    if (d < 1.6) {
      if (o.heal) { heal(o.heal); popDmg(P.pos.x, 4, P.pos.z, '+' + o.heal, 'heal'); }
      else gainXp(o.xp);   // M4：拾取物改回加經驗（M3 曾改成廢料，隨升級層一起拔掉）
      o.alive = false; scene.remove(o.m);
    }
  }
}
export function updateFx(dt) {
  var i, o;
  /* 預警層跑在**模擬時間**上：預警的長度是敵人的 `tTel`，兩者必須同一個時鐘，
     否則頓幀（`timeScale`）時預警會比攻擊早結束，玩家會覺得「明明閃掉了
     還是被打到」。 */
  updateWarnFx(dt);
  for (i = 0; i < debris.items.length; i++) {
    o = debris.items[i]; if (!o.alive) continue;
    o.life += dt;
    if (o.life >= o.max) { o.alive = false; scene.remove(o.m); continue; }
    o.v.y -= 30 * dt;
    o.m.position.addScaledVector(o.v, dt);
    if (o.m.position.y < 0.08) { o.m.position.y = 0.08; o.v.y = -o.v.y * 0.34; o.v.x *= 0.6; o.v.z *= 0.6; }
    o.m.rotation.x += o.av.x * dt; o.m.rotation.y += o.av.y * dt; o.m.rotation.z += o.av.z * dt;
    o.m.scale.setScalar(o.s0 * (1 - o.life / o.max));
  }
  for (i = 0; i < rings.items.length; i++) {
    o = rings.items[i]; if (!o.alive) continue;
    o.life += dt;
    if (o.life >= o.max) { o.alive = false; scene.remove(o.m); continue; }
    var t = o.life / o.max;
    var s = lerp(o.s0, o.s1, 1 - Math.pow(1 - t, 2.2));
    o.m.scale.set(s, s, s); o.m.material.opacity = (1 - t) * 0.85;
  }
  if (P.rig) for (i = 0; i < P.rig.muzzles.length; i++) {
    var mz = P.rig.muzzles[i]; if (!mz.visible) continue;
    mz.userData.t -= dt; if (mz.userData.t <= 0) mz.visible = false;
  }
  if (slashFx.visible) {
    slashFx.userData.life += dt;
    var sl = slashFx.userData.life / 0.22;
    slashFx.userData.mat.opacity = Math.max(0, 0.75 * (1 - sl));
    if (sl >= 1) slashFx.visible = false;
  }
}
